import type { TeamGroupScope } from '../types';

/**
 * Accordion persistence utilities for MyTeam dashboard.
 *
 * Default state: OPEN (true) — unknown keys resolve to open via resolveOpenState.
 * State map is stored under MY_TEAM_ACCORDION_STATE in localStorage via utils/memory.
 */
export type AccordionStateMap = Record<string, boolean>;

/**
 * Generates a unique accordion key for a group.
 * @param relationType - The group's relation type (e.g. 'teamManager', 'leaveIssuer')
 * @param scope - Optional scope with type, id, and name
 * @returns Key in format `${relationType}::${type}:${id}:${name}` (scope part empty if no scope)
 */
export const getGroupAccordionKey = (relationType: string, scope?: TeamGroupScope): string => {
  const scopePart = scope?.name != null ? `${scope.type ?? ''}:${scope.id ?? ''}:${scope.name}` : '';

  return `${relationType}::${scopePart}`;
};

/**
 * Resolves open/closed state for each group ID from the stored map.
 * @param groupIds - Current rendered group IDs (from displayGroups)
 * @param storedMap - Persisted state from localStorage
 * @returns Map of groupId -> boolean (true = open). Unknown keys default to open (true).
 */
export const resolveOpenState = (groupIds: string[], storedMap: AccordionStateMap): Record<string, boolean> => {
  return Object.fromEntries(groupIds.map((id) => [id, storedMap[id] ?? true]));
};

/**
 * Returns a new state map with a single group's open state toggled.
 * @param storedMap - Current persisted state map
 * @param groupId - Group identifier to toggle
 * @param isOpen - New open state for the group
 * @returns New map with only the specified groupId updated; original map unmutated.
 */
export const applyToggle = (storedMap: AccordionStateMap, groupId: string, isOpen: boolean): AccordionStateMap => {
  return { ...storedMap, [groupId]: isOpen };
};

/**
 * Returns a new state map with all provided group IDs set to the same open state.
 * @param storedMap - Current persisted state map
 * @param groupIds - Group identifiers to update
 * @param isOpen - Open state to apply to all (true = expand all, false = collapse all)
 * @returns New map with specified groupIds updated; other entries preserved; original unmutated.
 */
export const applyBulk = (storedMap: AccordionStateMap, groupIds: string[], isOpen: boolean): AccordionStateMap => {
  return { ...storedMap, ...Object.fromEntries(groupIds.map((id) => [id, isOpen])) };
};
